import type { StyleLibrary } from "schemas/generated/project";

import * as React from "react";

import { useNextDevServer } from "@v2/features/page-renderer/components/code-execution/context";
import { useSyncedSandboxStateUrl } from "@v2/store/SandboxStateStoreContext";

import { normalizeRoute } from "./utils";

const MAX_RETRIES = 15;

export function usePage(opts: {
  pageRoute: string;
  overrideStyleLibrary: StyleLibrary | null;
  viewingVersionId: string | null;
  forceSkipSandboxSync?: boolean;
}) {
  const {
    pageRoute,
    overrideStyleLibrary,
    viewingVersionId,
    forceSkipSandboxSync,
  } = opts;

  const [retryCount, setRetryCount] = React.useState(0);

  const {
    loadedRoute,
    iframeError,
    refreshEpoch,
    sandboxClientSideErrors,
    clearSandboxClientSideError,
  } = useNextDevServer();

  const normalizedRoute = React.useMemo(
    () => normalizeRoute(pageRoute),
    [pageRoute],
  );

  const url = useSyncedSandboxStateUrl({
    overrideStyleLibrary,
    viewingVersionId,
    forceSkip: forceSkipSandboxSync,
  });

  const pageUrl = React.useMemo(() => {
    return url && normalizedRoute !== undefined
      ? `${url}/${encodeURI(normalizedRoute)}`
      : undefined;
  }, [normalizedRoute, url]);

  // Note (Nick, 2026-01-26, REPL-22167): Get client-side error for this route if it exists
  const sandboxClientSideError = sandboxClientSideErrors.get(normalizedRoute);

  // NOTE (Nick, 2026-02-02): 500 errors (e.g. module-not-found) should show
  // the fix-it UI immediately without retrying. 404s are retried (turbopack
  // race condition). Client-side errors also count as errors.
  const hasPageNotFoundError =
    iframeError?.route === normalizedRoute &&
    iframeError.status === "pageNotFound";
  const hasPageLoadError =
    iframeError?.route === normalizedRoute &&
    iframeError.status === "pageLoadError";

  // NOTE (Nick, 2026-02-03): When we have a pageLoadError (500) for this
  // route, stop showing the loading bar. The error UI should replace it,
  // not overlap with it. Without this, the loading bar stays visible
  // because loadedRoute never gets set when the page 500s.
  const isLoading = normalizedRoute !== loadedRoute && !hasPageLoadError;

  const isError =
    hasPageLoadError ||
    (hasPageNotFoundError && retryCount >= MAX_RETRIES) ||
    (!isLoading && Boolean(sandboxClientSideError));

  // NOTE (Nick, 2026-02-02): Only retry when the iframe has reported a pageNotFound
  // for this route (turbopack hasn't compiled it yet). Don't retry on pageLoadErrors
  // or when we're just waiting for the initial load.
  React.useEffect(() => {
    if (
      !hasPageNotFoundError ||
      hasPageLoadError ||
      retryCount >= MAX_RETRIES
    ) {
      return;
    }

    const timeout = setTimeout(() => {
      setRetryCount((prev) => prev + 1);
    }, 400);

    return () => clearTimeout(timeout);
  }, [hasPageNotFoundError, hasPageLoadError, retryCount]);

  // NOTE (Nick, 2026-02-02): Reset retry count when the desired route changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: normalizedRoute is intentionally used as a trigger to reset retry count when the route changes
  React.useEffect(() => {
    setRetryCount(0);
  }, [normalizedRoute]);

  // Note (Nick, 2026-01-26, REPL-22167): Create a stable callback that clears the error for this route
  const clearError = React.useCallback(() => {
    clearSandboxClientSideError(normalizedRoute);
  }, [clearSandboxClientSideError, normalizedRoute]);

  return {
    pageUrl,
    isLoading,
    isError,
    iframeKey: `${retryCount + refreshEpoch}`,
    sandboxClientSideError,
    clearSandboxClientSideError: clearError,
  } as const;
}
